import { Company } from '@shared/models/company';
import { Person } from '@shared/models/person';
import { InvoicePosition } from '@shared/models/invoice-position';
import { WinterStorageBoatAssignment } from '@shared/models/winter-storage-boat-assignment';
import { WinterStorageAssignmentDialogFormValue } from '@sharedComponents/dialogs/winter-storage-assignments/add-winter-storage-assignment-dialog/winter-storage-assignment-dialog-payload.helper';

export interface WinterStorageBillingFormValue {
    deviatingInvoiceRecipient?: boolean;
    invoiceRecipientType?: 'person' | 'company';
    personForm?: { person?: Person } & Partial<Person>;
    companyForm?: { company?: Company } & Partial<Company>;
    subscriptionPeriodForm?: {
        fromDate?: Date;
        toDate?: Date;
        lastDayOfMonth?: boolean;
        payableOption?: string;
        vestingPeriodFrom?: Date;
        vestingPeriodUntil?: Date;
    };
}

export interface WinterStorageBillingPayload {
    positions: InvoicePosition[];
    subscriptionPeriod: WinterStorageBillingFormValue['subscriptionPeriodForm'] | null;
    person: Person | null;
    company: Company | null;
}

export function buildDraftWinterStorageAssignment(
    payload: WinterStorageAssignmentDialogFormValue
): WinterStorageBoatAssignment {
    return {
        from: payload.from as any,
        to: payload.to as any,
        assignedWidth: payload.assignedWidth,
        assignedLength: payload.assignedLength,
        assignedArea: payload.assignedArea,
        position: payload.position,
        helperTools: payload.helperTools,
        note: payload.note,
        boat: payload.boat,
        person: payload.person,
        company: payload.company,
        customAsset: payload.customAsset,
        winterStorage: payload.winterStorage,
        assetType: payload.assetType,
        assetOwnerType: payload.assetOwnerType,
    } as WinterStorageBoatAssignment;
}

export function isWinterStorageSubscriptionBilling(assignment: WinterStorageBoatAssignment): boolean {
    return !assignment?.to;
}

export function resolveWinterStorageBillingRecipient(
    formValue: WinterStorageBillingFormValue,
    invoiceRecipientType: 'person' | 'company',
    winterStorageAssignment: WinterStorageBoatAssignment
): { person: Person | null; company: Company | null } {
    const {personForm, companyForm, deviatingInvoiceRecipient} = formValue;

    let person: Person = null;
    let company: Company = null;

    if (!deviatingInvoiceRecipient) {
        if (winterStorageAssignment.person?.id) {
            person = winterStorageAssignment.person;
        } else if (winterStorageAssignment.company?.id) {
            company = winterStorageAssignment.company;
        }
    } else if (invoiceRecipientType === 'person') {
        if (personForm?.person?.id) {
            person = personForm.person;
        } else if (personForm) {
            const copy = {...personForm};
            delete copy.person;
            person = copy as Person;
        }
    } else if (invoiceRecipientType === 'company') {
        if (companyForm?.company?.id) {
            company = companyForm.company;
        } else if (companyForm) {
            const copy = {...companyForm};
            delete copy.company;
            company = copy as Company;
        }
    }

    return {person, company};
}

export function buildWinterStorageBillingPayload(
    formValue: WinterStorageBillingFormValue,
    invoiceRecipientType: 'person' | 'company',
    winterStorageAssignment: WinterStorageBoatAssignment,
    positions: InvoicePosition[],
    subscription: boolean,
    subscriptionPeriodFormValue: WinterStorageBillingFormValue['subscriptionPeriodForm'] | null
): WinterStorageBillingPayload {
    const {person, company} = resolveWinterStorageBillingRecipient(
        formValue,
        invoiceRecipientType,
        winterStorageAssignment
    );

    return {
        positions,
        subscriptionPeriod: subscription ? subscriptionPeriodFormValue : null,
        person,
        company,
    };
}
